import {
  ParamMarkReportSyncStatus,
  ParamsCreateHISReport,
  ParamsCreateReport,
  ParamsUpdateApproveReport,
  ParamsUpdateCancelReport,
  ParamsUpdateSubmitReport,
  createHISReport,
  getApprover,
  getLatestReportByOrder,
  getOrderReport,
  getReportByVersionId,
  getReportVersions,
  getReports,
  getStudyLatestReport,
  getTestInformation,
  markReportSyncStatus,
  updateApproveReport,
  updateCancelReport,
  updateEditReport,
  updateSubmitReport,
} from './../apis/report.api';

import {
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { sortObjectKeys } from '@vindr/utils/helpers/common.helper';
import { AxiosError } from 'axios';
import { OrderWorklist, ResponseGetOrderWorklist } from '../apis/order.api';
import { PrintReport, getPrintData } from '../apis/print.api';
import { createReport } from '../apis/report.api';
import { ResponseFetchStudyList } from '../apis/study.api';
import { queryKey } from '../constants/queryClientKey';
import { DefaultErrorResponse } from '../interfaces';

export function useGetReports(
  params: Parameters<typeof getReports>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.reports, sortObjectKeys(params)],
    () => getReports(params),
    options
  ) as UseQueryResult<Awaited<ReturnType<typeof getReports>>>;
}

export function useGetApprover(
  params: Parameters<typeof getApprover>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.approver, sortObjectKeys(params)],
    () => getApprover(params),
    options
  ) as UseQueryResult<Awaited<ReturnType<typeof getApprover>>>;
}

export function useGetListReport(
  selectedRowKeys:
    | ResponseFetchStudyList['data']
    | ResponseGetOrderWorklist['data'],
  options?: UseQueryOptions
) {
  return useQueries({
    queries: selectedRowKeys.map((row, index) => ({
      queryKey: [queryKey.print, sortObjectKeys(row)],
      queryFn: () =>
        getPrintData({
          row,
          params: {
            study: (selectedRowKeys as ResponseFetchStudyList['data'])[index],
          },
        }),
      ...options,
    })),
  }) as UseQueryResult<PrintReport>[];
}

export function useGetLatestReportFromOrder(
  selectedOrderWorklist: OrderWorklist,
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.print, sortObjectKeys(selectedOrderWorklist)],
    () => getPrintData({ row: selectedOrderWorklist }),
    options
  ) as UseQueryResult<PrintReport>;
}

export function useCreateReport() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: ParamsCreateReport) => createReport(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.reports]);
        queryClient.invalidateQueries([queryKey.orders]);
        queryClient.invalidateQueries([queryKey.orderDetail]);
        queryClient.invalidateQueries([queryKey.assigns_interval]);
        queryClient.invalidateQueries([queryKey.assigns]);
        queryClient.invalidateQueries([queryKey.latest_report_by_order]);
        queryClient.invalidateQueries([queryKey.report_by_version_id]);
        queryClient.invalidateQueries([queryKey.report_detail]);
        queryClient.invalidateQueries([queryKey.report_versions]);
      },
      onError: (err) => {
        console.error('useCreateReport:', err);
      },
    }
  );
}

export function useUpdateApproveReport() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: string; data: ParamsUpdateApproveReport }) =>
      updateApproveReport(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.reports]);
        queryClient.invalidateQueries([queryKey.orders]);
        queryClient.invalidateQueries([queryKey.orderDetail]);
        queryClient.invalidateQueries([queryKey.assigns_interval]);
        queryClient.invalidateQueries([queryKey.assigns]);
        queryClient.invalidateQueries([queryKey.latest_report_by_order]);
        queryClient.invalidateQueries([queryKey.report_by_version_id]);
        queryClient.invalidateQueries([queryKey.report_detail]);
        queryClient.invalidateQueries([queryKey.report_versions]);
      },

      onError: (err) => {
        console.error('useUpdateApproveReport:', err);
      },
    }
  );
}

export function useUpdateEditReport() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      id,
      data,
    }: {
      id: string;
      data?: Parameters<typeof updateEditReport>['1'];
    }) => updateEditReport(id, data),
    {
      onSuccess: () => {
        console.log('editing');
        queryClient.invalidateQueries([queryKey.reports]);
        queryClient.invalidateQueries([queryKey.orders]);
        queryClient.invalidateQueries([queryKey.orderDetail]);
        queryClient.invalidateQueries([queryKey.assigns_interval]);
        queryClient.invalidateQueries([queryKey.assigns]);
        queryClient.invalidateQueries([queryKey.print]);
        queryClient.invalidateQueries([queryKey.approver]);
        queryClient.invalidateQueries([queryKey.study_detail]);
        queryClient.invalidateQueries([queryKey.institution]);
        queryClient.invalidateQueries([queryKey.latest_report_by_order]);
        queryClient.invalidateQueries([queryKey.report_by_version_id]);
        queryClient.invalidateQueries([queryKey.report_detail]);
        queryClient.invalidateQueries([queryKey.report_versions]);
      },
    }
  );
}

export function useUpdateSubmitReport() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: string; data: ParamsUpdateSubmitReport }) =>
      updateSubmitReport(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.reports]);
        queryClient.invalidateQueries([queryKey.orders]);
        queryClient.invalidateQueries([queryKey.orderDetail]);
        queryClient.invalidateQueries([queryKey.assigns_interval]);
        queryClient.invalidateQueries([queryKey.assigns]);
        queryClient.invalidateQueries([queryKey.print]);
        queryClient.invalidateQueries([queryKey.approver]);
        queryClient.invalidateQueries([queryKey.study_detail]);
        queryClient.invalidateQueries([queryKey.institution]);
        queryClient.invalidateQueries([queryKey.latest_report_by_order]);
        queryClient.invalidateQueries([queryKey.report_by_version_id]);
        queryClient.invalidateQueries([queryKey.report_detail]);
        queryClient.invalidateQueries([queryKey.report_versions]);
      },

      onError: (err) => {
        console.error('useUpdateSubmitReport:', err);
      },
    }
  );
}

export function useUpdateCancelReport() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: string; data?: ParamsUpdateCancelReport }) =>
      updateCancelReport(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.reports]);
        queryClient.invalidateQueries([queryKey.orders]);
        queryClient.invalidateQueries([queryKey.orderDetail]);
        queryClient.invalidateQueries([queryKey.assigns_interval]);
        queryClient.invalidateQueries([queryKey.assigns]);
        queryClient.invalidateQueries([queryKey.print]);
        queryClient.invalidateQueries([queryKey.approver]);
        queryClient.invalidateQueries([queryKey.study_detail]);
        queryClient.invalidateQueries([queryKey.institution]);
        queryClient.invalidateQueries([queryKey.latest_report_by_order]);
        queryClient.invalidateQueries([queryKey.report_by_version_id]);
        queryClient.invalidateQueries([queryKey.report_detail]);
        queryClient.invalidateQueries([queryKey.report_versions]);
      },

      onError: (err) => {
        console.error('useUpdateSubmitReport:', err);
      },
    }
  );
}

export function useCreateHISReport() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: ParamsCreateHISReport) => createHISReport(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.reports]);
        queryClient.invalidateQueries([queryKey.orders]);
        queryClient.invalidateQueries([queryKey.orderDetail]);
        queryClient.invalidateQueries([queryKey.assigns_interval]);
        queryClient.invalidateQueries([queryKey.assigns]);
      },
      onError: (err) => {
        console.error('useCreateHISReport:', err);
      },
    }
  );
}

export function useMarkReportSyncStatus() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: string; data?: ParamMarkReportSyncStatus }) =>
      markReportSyncStatus(id, data),
    {
      onSuccess: () => {
        console.log('editing');
        queryClient.invalidateQueries([queryKey.reports]);
        queryClient.invalidateQueries([queryKey.orders]);
        queryClient.invalidateQueries([queryKey.orderDetail]);
        queryClient.invalidateQueries([queryKey.assigns_interval]);
        queryClient.invalidateQueries([queryKey.assigns]);
      },

      onError: (err) => {
        console.error('useMarkReportSyncStatus:', err);
      },
    }
  );
}

export function useGetOrderReport(
  params: Parameters<typeof getOrderReport>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.order_report, sortObjectKeys(params)],
    () => getOrderReport(params),
    options
  ) as UseQueryResult<Awaited<ReturnType<typeof getOrderReport>>>;
}

export function useGetStudyLatestReport(
  params: Parameters<typeof getStudyLatestReport>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.study_latest_report, params],
    () => getStudyLatestReport(params),
    options
  ) as UseQueryResult<Awaited<ReturnType<typeof getStudyLatestReport>>>;
}

export function useGetLatestReportByOrder(
  params: Parameters<typeof getLatestReportByOrder>[0],
  options?: UseQueryOptions<
    Awaited<ReturnType<typeof getLatestReportByOrder>>,
    AxiosError<DefaultErrorResponse>
  >
) {
  return useQuery(
    options?.queryKey || [queryKey.latest_report_by_order, params],
    () => getLatestReportByOrder(params),
    options
  );
}

export function useGetReportByVersionId(
  params: Parameters<typeof getReportByVersionId>[0],
  options?: UseQueryOptions<Awaited<ReturnType<typeof getReportByVersionId>>>
) {
  return useQuery(
    options?.queryKey || [queryKey.report_by_version_id, params],
    () => getReportByVersionId(params),
    options
  );
}

export function useGetReportVersions(
  params: Parameters<typeof getReportVersions>[0],
  options?: UseQueryOptions<Awaited<ReturnType<typeof getReportVersions>>>
) {
  return useQuery(
    options?.queryKey || [queryKey.report_versions, params],
    () => getReportVersions(params),
    options
  );
}

export function useGetTestInformation(
  params: Parameters<typeof getTestInformation>[0],
  options?: UseQueryOptions<Awaited<ReturnType<typeof getTestInformation>>>
) {
  return useQuery(
    options?.queryKey || [queryKey.test_information, params],
    () => getTestInformation(params),
    options
  );
}
